import { StacksManager } from '@/components/shared/stacks/StacksManager';
import type { StackChallengeSummary } from '@/config/objects/placements';
import type { TemplateChallengeLink } from '@/config/objects/templates';
import { useTemplateContext } from '@/pages/templates/useTemplateContext';
import { Skeleton } from '@startupquickstart/frontend';

/**
 * Template "challenges" are library links — the display name lives on the
 * linked library challenge, and the pin may be a per-template override or
 * inherited from the library location.
 */
function normalizeTemplateChallenges(items: unknown[]): StackChallengeSummary[] {
  return (items as TemplateChallengeLink[]).map((link) => ({
    id: link.id,
    name: link.libraryChallenge?.name ?? 'Untitled challenge',
    latitude: link.latitude ?? link.libraryChallenge?.latitude ?? null,
    longitude: link.longitude ?? link.libraryChallenge?.longitude ?? null,
    stackId: link.stackId ?? null,
  }));
}

/**
 * Template Stacks tab — thin wrapper around the shared owner-scoped
 * StacksManager. Templates have no lifecycle, so structure is always
 * editable.
 */
export function TemplateStacksTab() {
  const { template, isLoading } = useTemplateContext();

  if (isLoading || !template) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
    );
  }

  return (
    <StacksManager
      ownerId={template.id}
      apiBase={`/templates/${template.id}`}
      keyPrefix="Template"
      structureEditable
      normalizeChallenges={normalizeTemplateChallenges}
    />
  );
}
